import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import {
  BarChart,
  BarStackChart,
  ColumnChart,
  FAST_TRACK_CHART_COLORS,
  formatData,
  ScatterChart,
  TableData,
  tooltipFormatter,
  tooltipFormatterxy,
  twoDataLabelFormatter,
} from '@utilities';

import { getPptStructuredData, getColorByVariant, getTableColumns } from '../../utilities/helpers';

import Tree from '../../../Report/components/Tab/components/Visualizations/components/Tree';
import { transformTreeFastTrackData } from '../../../Report/components/Tab/components/Visualizations/utilities/tree_helpers';
import styles from './_index.module.scss';

const Visualization = ({ dataset, exportData, isExcluded, setDownloadData, topicId }) => {
  const { series = [], type = '', xAxis = {}, yAxis = {} } = dataset ?? {};
  const [chartInstance, setChartInstance] = useState(null);

  const onChartCallback = (chart) => {
    setChartInstance(chart);
  };

  const updateDownloadData = (content) => {
    if (content?.type === 'chart-image' && !chartInstance) {
      return;
    }

    setDownloadData((prevData) => {
      const data = new Map(prevData);
      const existingTopic = prevData?.get(topicId);
      if (isExcluded) {
        data.set(topicId, {
          ...existingTopic,
          content: [],
        });
      } else {
        const newContentChart = ['chart-image', 'brand-tree-v2'].includes(content?.type)
          ? chartInstance
          : content;
        const newContent = {
          data: newContentChart,
          readAs: dataset?.cleanReadAs || dataset?.readAs,
        };

        const isDuplicate = existingTopic?.content?.some((existing) => {
          if (['chart-image', 'brand-tree-v2'].includes(content?.type))
            return existing?.data === newContentChart;
          return JSON.stringify(existing) === JSON.stringify(newContent);
        });
        if (isDuplicate) return prevData;

        data.set(topicId, {
          ...existingTopic,
          content: [...(existingTopic?.content || []), newContent],
          id: topicId,
          type: content?.type,
        });
      }

      return data;
    });
  };

  useEffect(() => {
    if (!topicId || !dataset) return;
    const structuredData = getPptStructuredData(dataset);
    if (!['chart-image', 'brand-tree-v2'].includes(structuredData?.type)) {
      updateDownloadData(structuredData);
    }
  }, [dataset, isExcluded, topicId]);

  useEffect(() => {
    // Need useEffect for chart-image as the chart ref needs to be set before
    if (!chartInstance || !topicId) return;
    const structuredData = getPptStructuredData(dataset);
    updateDownloadData(structuredData);
  }, [chartInstance, isExcluded, topicId]);

  switch (type) {
    case 'bar-chart': {
      const format = yAxis?.labels?.format || '0.0';
      return (
        <BarChart
          className={styles['visualization']}
          colors={FAST_TRACK_CHART_COLORS}
          hasExportButtonText={false}
          onChartCallback={onChartCallback}
          plotOptions={{
            bar: {
              dataLabels: {
                formatter: function () {
                  return formatData({
                    format,
                    value: this.y,
                  });
                },
              },
            },
            series: {
              border: 'none',
              borderRadius: 3,
              groupPadding: 0.2,
            },
          }}
          series={series}
          tooltip={{
            formatter: function () {
              return tooltipFormatter({ data: this, format });
            },
          }}
          xAxis={xAxis}
          yAxis={{
            labels: {
              formatter: function () {
                return formatData({ format, value: this.value });
              },
            },
          }}
        />
      );
    }
    case 'bar-chart-two-labels': {
      const format = yAxis?.labels?.format || '0.0';
      const formattedSeriesWithDataLabels = series.map((seriesItem) => ({
        ...seriesItem,
        dataLabels: [
          {
            format: null,
            formatter: function () {
              return formatData({
                format: seriesItem.dataLabels?.[0]?.format,
                value: this.y,
              });
            },
          },
          {
            align: 'right',
            alignTo: 'plotEdges',
            crop: false,
            format: null,
            formatter: function () {
              return twoDataLabelFormatter({
                format: seriesItem.dataLabels?.[1]?.format,
                value: this.y2,
                yDelta: this.yDelta,
              });
            },
            overflow: 'allow',
            padding: -45,
            useHTML: true,
          },
        ],
      }));

      return (
        <BarChart
          chart={{
            marginRight: 45,
          }}
          className={styles['visualization']}
          colors={FAST_TRACK_CHART_COLORS}
          hasExportButtonText={false}
          onChartCallback={onChartCallback}
          plotOptions={{
            series: {
              border: 'none',
              borderRadius: 3,
              groupPadding: 0.2,
            },
          }}
          series={formattedSeriesWithDataLabels}
          tooltip={{
            formatter: function () {
              return tooltipFormatter({ data: this, format });
            },
          }}
          xAxis={xAxis}
          yAxis={{
            labels: {
              formatter: function () {
                return formatData({ format, value: this.value });
              },
            },
            showLastLabel: false,
          }}
        />
      );
    }
    case 'bar-stack-chart': {
      const format = yAxis?.labels?.format || '0.0';

      return (
        <BarStackChart
          chart={{
            marginLeft: 150,
            spacingRight: 0,
          }}
          className={styles['visualization']}
          colors={FAST_TRACK_CHART_COLORS}
          hasExportButtonText={false}
          onChartCallback={onChartCallback}
          plotOptions={{
            series: {
              dataLabels: {
                enabled: true,
                formatter: function () {
                  return formatData({
                    format,
                    value: this.y,
                  });
                },
                inside: true,
              },
            },
          }}
          series={series}
          tooltip={{
            formatter: function () {
              return tooltipFormatter({ data: this, format });
            },
          }}
          xAxis={{ ...xAxis, lineWidth: 0 }}
          yAxis={{
            gridLineWidth: 0,
            labels: {
              enabled: false,
            },
            width: 815,
          }}
        />
      );
    }
    case 'brand-tree-v2':
    case 'ebsova-tree': {
      const treeData = transformTreeFastTrackData(dataset.nodes, type);
      return (
        <div className={styles['visualization']}>
          <Tree
            data={treeData}
            exportData={exportData}
            nodeClassName={type === 'ebsova-tree' ? styles['fasttrack-ebsova-tree'] : ''}
            onChartCallback={onChartCallback}
            type="tree"
          />
        </div>
      );
    }
    case 'column-chart': {
      const format = yAxis?.labels?.format || '0.0';
      const topSeries = series.map((seriesItem) => ({
        ...seriesItem,
        data: seriesItem.data,
      }));
      const topXAxis = {
        ...xAxis,
        categories: xAxis?.categories || [],
      };

      return (
        <ColumnChart
          className={styles['visualization']}
          colors={FAST_TRACK_CHART_COLORS}
          hasExportButtonText={false}
          onChartCallback={onChartCallback}
          series={topSeries}
          tooltip={{
            formatter: function () {
              return tooltipFormatter({ data: this, format });
            },
          }}
          xAxis={topXAxis}
          yAxis={{
            labels: {
              formatter: function () {
                return formatData({ format, value: this.value });
              },
            },
          }}
        />
      );
    }
    case 'scatter-chart': {
      const formattedSeries = series?.map((row) => ({
        ...row,
        data: row.data.map((value) => ({
          ...value,
          color: getColorByVariant(value.colorType),
          dataLabels: {
            backgroundColor: getColorByVariant(value.colorType),
            borderColor: getColorByVariant(value.colorType),
            format: '{point.name}',
            ...row.dataLabels,
            ...value.dataLabels,
          },
        })),
      }));
      const formatX = xAxis?.labels?.format || '0%';
      const formatY = yAxis?.labels?.format || '$0,0.00';

      return (
        <ScatterChart
          className={styles['visualization']}
          hasExportButtonText={false}
          legend={false}
          onChartCallback={onChartCallback}
          series={formattedSeries}
          tooltip={{
            formatter: function () {
              return tooltipFormatterxy({
                data: this,
                xAxis,
                yAxis,
              });
            },
          }}
          xAxis={{
            labels: {
              formatter: function () {
                return formatData({ format: formatX, value: this.value });
              },
            },
            max: xAxis?.max,
            plotLines: [
              // Only considering plotlines for focus product point
              {
                color: getColorByVariant('primary'),
                dashStyle: 'Dot',
                value: xAxis?.plotLines[0]?.value,
                width: 2,
              },
            ],
            title: {
              align: 'middle',
              margin: 20,
              text: xAxis?.title?.text,
            },
          }}
          yAxis={{
            labels: {
              formatter: function () {
                return formatData({ format: formatY, value: this.value });
              },
            },
            max: yAxis?.max,
            plotLines: [
              {
                color: getColorByVariant('primary'),
                dashStyle: 'Dot',
                value: yAxis?.plotLines[0]?.value,
                width: 2,
              },
            ],
            title: {
              align: 'middle',
              margin: 20,
              rotation: 270,
              text: yAxis?.title?.text,
            },
          }}
        />
      );
    }
    case 'table':
      const columns = getTableColumns(dataset.columns);

      return (
        <div className={styles['visualization']}>
          <TableData columns={columns} data={dataset.data} shouldTruncateHeaders={false} />
        </div>
      );
    default:
      return <span>{`${type} Visualization Unavailable`}</span>;
  }
};

Visualization.propTypes = {
  dataset: PropTypes.object.isRequired,
  exportData: PropTypes.object,
  isExcluded: PropTypes.bool,
  setDownloadData: PropTypes.func,
  topicId: PropTypes.number,
};
export default Visualization;
